import React, {useContext, useMemo, createContext, useReducer, useCallback, Dispatch} from "react"
import tracking from "@irati-tracking/wallet/rewardPointsWallet"
import {useBudgetsList} from "@irati/wallet/hooks/budget"
import {Budget, BudgetUnassigned, BulkRewardStatus} from "@irati/types"
import FormatMessage from "@irati/utils/lang/types/FormatMessage"
import flexiPointService from "@irati/wallet/services/flexiRewards"
import {DistributeRewardImportType, DistributeRewardRecipient} from "../../enums/DistributeRewardTypes"
import {BulkRewardEvents} from "../../enums/MixpanelEvents"
import {createBulkAndPollStatus, executeManualReward} from "./handlers"
import {steps, DistributeRewardState, DistributeRewardActionTypes, UserRecognition, Step} from "./actions"
import DistributeRewardReducer from "./reducer"

interface ProviderProps {
  children: React.ReactNode
  initial: Intital
  showNotification: (...info: Parameters<FormatMessage>) => void
}

interface Intital {
  recognition: UserRecognition
  /** initial step as set from the path */
  step?: Step
  /** initial staet as set from the search params */
  search: {
    recipient?: DistributeRewardRecipient
    template?: string
  }
}

const INITIAL_STATE = ({
  recognition,
  step,
  search: {recipient = DistributeRewardRecipient.NONE, template}
}: Intital): DistributeRewardState => {
  const initialStep = step && steps.includes(step) ? step : "recipients"
  const recipientType = DistributeRewardRecipient[recipient] || DistributeRewardRecipient.NONE

  return {
    step: initialStep,
    pointsPerUser: 0,
    message: "",
    recipientType,
    rewardImportType: DistributeRewardImportType.MANUAL_ENTER,
    destinationUsers: [],
    userEmails: [],
    recognition,
    bulkRewardState: {
      failedEmails: [],
      uuid: null,
      csvError: null
    },
    modalConfirmIsShown: false,
    modalErrorState: {
      title: "",
      content: "",
      buttonText: "",
      show: false
    },
    checkUnassigned: false,
    ...(template && {rewardTemplate: {uuid: template}})
  }
}

export type UseRewardsDistributionState = Prettify<
  DistributeRewardState & {
    category?: Budget
    unassigned?: BudgetUnassigned
  }
>

export interface UseRewardsDistributionActions {
  dispatch: Dispatch<DistributeRewardActionTypes>
  confirmBulkReward: (payload: UseRewardsDistributionState) => Promise<void>
  sendManualReward: (payload: UseRewardsDistributionState) => Promise<void>
  createBulkReward: (payload: UseRewardsDistributionState) => Promise<void>
  modifyBulkReward: (payload: UseRewardsDistributionState) => Promise<void>
}

const distributeRewardsContext = createContext<UseRewardsDistributionState | null>(null)
const distributeRewardsActionsContext = createContext<UseRewardsDistributionActions | null>(null)

export const DistributeRewardsProvider = React.memo(DistributeRewardsProviderComp)
function DistributeRewardsProviderComp({children, initial, showNotification}: ProviderProps) {
  const [state, dispatch] = useReducer(DistributeRewardReducer, INITIAL_STATE(initial))
  const {data, refetch: refetchBudgets} = useBudgetsList()

  const {budgetCategory, checkUnassigned} = state

  const category = useMemo(() => data?.budgets.categories.find((c) => c.name === budgetCategory), [data, budgetCategory])
  const unassigned = useMemo(
    () => (checkUnassigned || data?.budgets.unassigned.name === budgetCategory ? data?.budgets.unassigned : undefined),
    [data, checkUnassigned, budgetCategory]
  )

  const hookState = useMemo(() => ({...state, category, unassigned}), [state, category, unassigned])

  const showGenericError = useCallback(() => {
    dispatch({
      type: "SET_MODAL_ERROR_STATE",
      payload: {
        show: true,
        title: "wallet.adminReward.bulkRewards.errorModal.payment.title",
        content: "wallet.adminReward.bulkRewards.errorModal.payment.content",
        buttonText: "wallet.adminReward.bulkRewards.errorModal.payment.cta"
      }
    })
  }, [dispatch])

  const sendManualReward = useCallback(
    async (payload: UseRewardsDistributionState) => {
      const count = Math.round(payload.pointsPerUser) * payload.destinationUsers.length

      await executeManualReward({
        state: payload,
        notifications: {
          assignedPoints: () => showNotification({id: "wallet.reward.admin.notification.assignedPoints"}, {count}),
          sendReward: () => showNotification({id: "wallet.reward.admin.notification.sendReward"}, {count})
        }
      })
      setTimeout(() => {
        refetchBudgets()
      }, 1000)
    },
    [refetchBudgets, showNotification]
  )

  const confirmBulkReward = useCallback(
    async (payload: Pick<DistributeRewardState, "bulkRewardState" | "message">) => {
      tracking.uploadCsv(BulkRewardEvents.SendReward)

      const {status} = await flexiPointService.confirmBulkReward({uuid: payload.bulkRewardState.uuid!, message: payload.message})

      if (hasBulkRewardError(status)) {
        tracking.rewardEmployeesError(status)
        showGenericError()
      } else {
        dispatch({
          type: "SET_CONFIRMATION_MODAL",
          payload: {modalConfirmIsShown: true}
        })
      }
    },
    [dispatch, showGenericError]
  )

  const createBulkReward = useCallback(
    async (payload: UseRewardsDistributionState) => {
      const {uuid, status} = await createBulkAndPollStatus(payload)

      if (hasBulkRewardError(status)) {
        tracking.rewardEmployeesError(status)
        showGenericError()
        return
      }

      const failedEmails = await flexiPointService.bulkRewardFailedEmails({uuid})

      dispatch({
        type: "SET_BULK_REWARD_STATE",
        payload: {csvError: null, uuid, failedEmails}
      })

      if (failedEmails.length) {
        tracking.uploadCsv(BulkRewardEvents.UserMatchError)
      } else {
        dispatch({type: "SET_STEP", payload: "message"})
      }
    },
    [dispatch, showGenericError]
  )

  const modifyBulkReward = useCallback(
    async (payload: UseRewardsDistributionState) => {
      const {status} = await flexiPointService.modifyBulkReward({
        input: {
          uuid: payload.bulkRewardState.uuid!,
          amount: payload.pointsPerUser * 100,
          message: payload.message,
          schedule: payload.schedule?.isScheduled
            ? {when: payload.schedule.utc!, originalTimezone: payload.schedule.timezone!}
            : undefined,
          budget: payload.category?.uuid
            ? {
                categoryId: payload.category?.uuid,
                useUnassigned: !!payload.checkUnassigned
              }
            : undefined,
          occasionId: payload.rewardTemplate?.uuid,
          title: payload.rewardTemplate ? payload.rewardTemplateTitle : undefined
        }
      })

      if (hasBulkRewardError(status)) {
        tracking.rewardEmployeesError(status)
        showGenericError()
      } else {
        dispatch({type: "SET_STEP", payload: "confirm"})
      }
    },
    [dispatch, showGenericError]
  )

  const actions = useMemo(
    () => ({dispatch, sendManualReward, confirmBulkReward, createBulkReward, modifyBulkReward}),
    [dispatch, sendManualReward, confirmBulkReward, createBulkReward, modifyBulkReward]
  )

  return (
    <distributeRewardsActionsContext.Provider value={actions}>
      <distributeRewardsContext.Provider value={hookState}>{children}</distributeRewardsContext.Provider>
    </distributeRewardsActionsContext.Provider>
  )
}

export function useRewardsDistribution() {
  const context = useContext(distributeRewardsContext)
  if (!context) throw new Error("useRewardsDistributionData must be used inside of a DistributeRewardsProvider")

  return context
}

export function useRewardsDistributionActions() {
  const context = useContext(distributeRewardsActionsContext)
  if (!context) throw new Error("useRewardsDistributionActions must be used inside of a DistributeRewardsProvider")

  return context
}

function hasBulkRewardError(status: BulkRewardStatus) {
  return status === BulkRewardStatus.Error || status === BulkRewardStatus.Unrecognized
}
